import React, { Suspense, startTransition } from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClientProvider, notifyManager } from '@tanstack/react-query'
import { queryClient } from '@/shared/lib/queryClientSingleton'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { RouterProvider } from 'react-router-dom'
import { router } from '@/router'
import { Toaster } from '@/shared/ui/toaster'
import { reportClientError } from '@/shared/lib/clientErrorReporter'
import { redactTelemetryEvent } from '@/shared/lib/redactUrl'
import { initStaleBuildDetector } from '@/shared/lib/staleBuildDetector'
import './index.css'

// Register the vite:preloadError listener early — before any lazy chunk loads —
// so stale-bundle failures show a "reload to continue" banner instead of a
// confusing MIME-type error in the console.
initStaleBuildDetector()

// Error tracking (Sentry/GlitchTip). Guarded on the build-time DSN: when
// VITE_SENTRY_DSN is unset (dev, or before Andras sets the CI variable) Sentry is
// never initialised and stays fully inert. The DSN is a public frontend value, so
// baking it into the bundle is expected. replaysOnErrorSampleRate:0 = no session
// replay (no extra bundle/PII); tracesSampleRate:0.1 samples performance spans.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN
// Resolve the Sentry environment at RUNTIME from the hostname: staging and prod are
// served from the SAME registry image (one build), so import.meta.env.MODE is
// "production" on both — which mis-tagged staging errors as `production`. The host is
// the only reliable per-deployment signal from a shared bundle.
function resolveSentryEnvironment(): string {
  const host = typeof window !== 'undefined' ? window.location.hostname : ''
  if (host === 'staging.studioflow.ch' || host.startsWith('staging.')) return 'staging'
  if (host === 'studioflow.ch' || host.endsWith('.studioflow.ch')) return 'production'
  return import.meta.env.MODE // local dev / preview / unknown host
}
/**
 * Sentry's React SDK parses ~58 KB of @sentry/core on every load, DSN or not,
 * because a static import runs whether or not the guard below is taken. It is now
 * loaded ONLY when there is a DSN to send to (IN-46 Phase 4.3).
 *
 * The React root's error hooks are created before this promise settles, so they
 * cannot close over a handler that does not exist yet. They call through
 * `sentryErrorHandler`, a late-bound reference that is undefined until the module
 * arrives — errors thrown in that window are simply not reported to Sentry, which
 * is exactly what happened before when no DSN was configured.
 */
let sentryErrorHandler: ((error: unknown, errorInfo: unknown) => void) | undefined

if (sentryDsn) {
  void import('@sentry/react').then(Sentry => {
    Sentry.init({
      dsn: sentryDsn,
      environment: resolveSentryEnvironment(),
      release: import.meta.env.VITE_APP_VERSION,
      tracesSampleRate: 0.1,
      replaysOnErrorSampleRate: 0,
      // Sentry reads window.location.href itself, so the capability token in an
      // invoice-pay / offer-accept / contract-accept / questionnaire / gallery
      // URL would reach a third party without any of our code handing it over.
      // Same redaction as the in-house reporter — see shared/lib/redactUrl.ts.
      beforeSend: redactTelemetryEvent,
      beforeSendTransaction: redactTelemetryEvent,
    })
    const handler = Sentry.reactErrorHandler()
    sentryErrorHandler = (error, errorInfo) =>
      (handler as (e: unknown, i: unknown) => void)(error, errorInfo)
  }).catch(() => {
    // Error reporting failing to load must never take the app with it.
  })
}

// Route all TanStack Query state-update notifications through React 18's
// startTransition so the browser scheduler can slice long reconciliation
// tasks into ≤5ms chunks. Without this, every batch of query results that
// lands while the main thread is busy creates a single long synchronous
// React re-render — measured as a 569ms task in Lighthouse TBT.
notifyManager.setScheduler(startTransition)
// Side-effect import — initialises i18next before React renders so
// components never hit a window where t() returns the raw key. The
// detector picks up navigator.language or localStorage on its own;
// useUiLocaleSync later overrides with the user's saved preference.
import '@/i18n'

// Register the service worker on load so the installed PWA always has an active
// SW — it powers web push and routes notification taps into the app (deep links),
// and makes the app a more complete install/deep-link target. It has no fetch
// handler, so there is no offline-cache / stale-bundle hazard. Best-effort:
// pushNotifications.ts also registers it lazily on push opt-in (idempotent).
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {
      /* best-effort — push/deep-link routing simply stays unavailable */
    })
  })
}

// Capture uncaught synchronous JS errors
window.onerror = (message, _source, _line, _col, error) => {
  reportClientError({
    message: String(message),
    stack: error?.stack,
    url: window.location.href,
    userAgent: navigator.userAgent,
  })
}

// Capture unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason
  const message =
    reason instanceof Error
      ? reason.message
      : String(reason ?? 'Unhandled promise rejection')
  reportClientError({
    message,
    stack: reason instanceof Error ? reason.stack : undefined,
    url: window.location.href,
    userAgent: navigator.userAgent,
  })
})

// React 19 root error hooks → Sentry. onCaughtError fires for errors an
// ErrorBoundary handles (incl. our top-level one); onUncaughtError for those that
// escape every boundary. Both no-op when Sentry has no DSN.
ReactDOM.createRoot(document.getElementById('root')!, {
  onUncaughtError: (error, errorInfo) => sentryErrorHandler?.(error, errorInfo),
  onCaughtError: (error, errorInfo) => sentryErrorHandler?.(error, errorInfo),
}).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Root Suspense boundary. Since IN-46 Phase 3 no locale is bundled, so the
          very first render can suspend while the detected language's core
          namespaces are in flight. Every lazy ROUTE already has its own boundary,
          but <Toaster/> and the router's own shell render outside them, and a
          suspend with no boundary above it is an uncaught error, not a fallback.
          The fallback is deliberately empty rather than a spinner or a translated
          "Loading…": it lasts one small chunk fetch, and a flash of chrome that
          then relayouts reads worse than a beat of nothing. */}
      <Suspense fallback={null}>
        <RouterProvider router={router} />
      </Suspense>
      <Toaster />
      {/* Its launcher is pulled below the app's overlay layer in index.css
          (`.tsqd-open-btn-container`) — it ships z-index 100000, which on a
          phone put dev chrome on top of every bottom sheet's action row. */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </React.StrictMode>
)
